import { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import ProjectsCarousel from '@/features/projects/ProjectsCarousel'
import Timeline from '@/features/timeline/Timeline'
import Skills from '@/features/skills/Skills'
import ContactForm from '@/features/contact/ContactForm'
import contact from '@/content/contact.json'
import profile from '@/content/profile.json'
import { ArrowUpRight, Check, Download, Github, Linkedin, Mail, Menu, Phone } from 'lucide-react'

type NavItem = { id: string; label: string }
type Contact = { email?: string; phone?: string; socials?: Record<string, string> }

export default function Lite() {
  const profileData = (profile as {
    title: string
    tagline: string
    focusTags: string[]
  }) || { title: '', tagline: '', focusTags: [] }
  const reduceMotion = useReducedMotion()
  const [open, setOpen] = useState(false)
  const [toast, setToast] = useState<null | { id: number; message: string }>(null)
  const contactData = contact as Contact
  const socials = contactData.socials ?? {}
  const cvHref = `${import.meta.env.BASE_URL}assets/CV_LATOUNDJI_Mourad.pdf`

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' })
  }

  useEffect(() => {
    const hash = window.location.hash?.replace('#', '')
    if (!hash) return
    window.setTimeout(() => scrollToSection(hash), 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!toast) return
    const t = window.setTimeout(() => setToast(null), 1600)
    return () => window.clearTimeout(t)
  }, [toast])

  const copyToClipboard = async (text: string, key: 'email' | 'phone') => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      // Fallback for older browsers / non-secure contexts
      const ta = document.createElement('textarea')
      ta.value = text
      ta.setAttribute('readonly', 'true')
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
    const msg = key === 'email' ? 'Email copié' : 'Téléphone copié'
    setToast({ id: Date.now(), message: msg })
  }

  const nav = useMemo<NavItem[]>(
    () => [
      { id: 'about', label: 'À propos' },
      { id: 'timeline', label: 'Parcours' },
      { id: 'projects', label: 'Projets' },
      { id: 'skills', label: 'Compétences' },
      { id: 'contact', label: 'Contact' }
    ],
    []
  )

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div id="top" className="lite lite-paper min-h-full text-slate-100">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-slate-800/80 bg-slate-950/45 backdrop-blur">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center gap-3">
          <a href="/">
          <div className="flex items-baseline gap-2">
            <div className="font-extrabold italic tracking-tight">Mourad Latoundji</div>
          </div>
          </a>

          <span
            className={
              'inline-flex items-center rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1 text-[11px] font-bold tracking-wide text-emerald-200'
            }
          >
            beta
    </span>
          
          <div className="ml-auto flex items-center gap-3">
            {socials.linkedin && (
              <a
                href={socials.linkedin}
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
                title="LinkedIn"
                className="inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-950/35 p-2 text-slate-100 hover:bg-slate-950/55 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
              >
                <Linkedin className="h-4 w-4 text-brand-300" />
              </a>
            )}
            {socials.github && (
              <a
                href={socials.github}
                target="_blank"
                rel="noreferrer"
                aria-label="GitHub"
                title="GitHub"
                className="inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-950/35 p-2 text-slate-100 hover:bg-slate-950/55 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
              >
                <Github className="h-4 w-4 text-brand-300" />
              </a>
            )}
            {contactData.phone && (
              <button
                type="button"
                aria-label="Copier le numéro"
                title="Copier le numéro"
                onClick={() => copyToClipboard(contactData.phone ?? '', 'phone')}
                className="hidden sm:inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-950/35 p-2 text-slate-100 hover:bg-slate-950/55 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
              >
                <Phone className="h-4 w-4 text-brand-300" />
                <span className="sr-only">Copier le numéro</span>
              </button>
            )}
            <button
              type="button"
              aria-label="Copier l’email"
              title="Copier l’email"
              onClick={() => copyToClipboard(contactData.email ?? '', 'email')}
              className="hidden sm:inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-950/35 p-2 text-slate-100 hover:bg-slate-950/55 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
            >
              <Mail className="h-4 w-4 text-brand-300" />
              <span className="sr-only">Copier l’email</span>
            </button>
            <a
              href={cvHref}
              download
              aria-label="Télécharger CV"
              title="Télécharger CV"
              className="hidden sm:inline-flex items-center justify-center rounded-xl bg-brand-500 p-2 text-slate-950 hover:bg-brand-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
            >
              <Download className="h-4 w-4" />
            </a>
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Ouvrir le menu"
              title="Menu"
              className="rounded-xl border border-slate-700 bg-slate-950/35 p-2 text-slate-100 hover:bg-slate-950/55 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <button
              type="button"
              aria-label="Fermer le menu"
              className="absolute inset-0 bg-black/55"
              onClick={() => setOpen(false)}
            />
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 320, damping: 28 }}
              className="absolute right-0 top-0 h-full w-[min(360px,92vw)] bg-slate-950/85 backdrop-blur border-l border-slate-800 p-5"
            >
              <div className="flex items-center gap-2">
                <div className="text-sm font-extrabold italic text-slate-100">Menu</div>
                <button
                  type="button"
                  className="ml-auto rounded-xl border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-950/65 transition-colors"
                  onClick={() => setOpen(false)}
                >
                  Fermer
                </button>
              </div>
              <nav className="mt-4 space-y-1">
                {nav.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => {
                      setOpen(false)
                      window.requestAnimationFrame(() => scrollToSection(n.id))
                    }}
                    className="block rounded-xl px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-900/40 transition-colors"
                  >
                    {n.label}
                  </button>
                ))}
              </nav>

              <div className="mt-6 space-y-2">
                <a
                  href={cvHref}
                  download
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-brand-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                >
                  <Download className="h-4 w-4" />
                  Télécharger CV
                </a>
                <div className="flex flex-wrap gap-2">
                  {socials.linkedin && (
                    <a
                      href={socials.linkedin}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-950/65 transition-colors"
                    >
                      <Linkedin className="h-4 w-4 text-brand-300" />
                      LinkedIn
                      <ArrowUpRight className="ml-auto h-4 w-4 text-slate-400" />
                    </a>
                  )}
                  {socials.github && (
                    <a
                      href={socials.github}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-950/65 transition-colors"
                    >
                      <Github className="h-4 w-4 text-brand-300" />
                      GitHub
                      <ArrowUpRight className="ml-auto h-4 w-4 text-slate-400" />
                    </a>
                  )}
                  {contactData.phone && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(contactData.phone ?? '', 'phone')}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-950/65 transition-colors"
                    >
                      <Phone className="h-4 w-4 text-brand-300" />
                      Téléphone
                    </button>
                  )}
                  {contactData.email && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(contactData.email ?? '', 'email')}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-950/65 transition-colors"
                    >
                      <Mail className="h-4 w-4 text-brand-300" />
                      Email
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-6 text-xs text-slate-400">
                Utilisez <kbd className="rounded bg-white/10 px-1">Esc</kbd> pour fermer.
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="mx-auto max-w-6xl px-4 py-10 space-y-16 relative z-10">
        {/* Hero (inspiré du layout Shamwwil, adapté à ton profil) */}
        <section
          id="about"
          className="scroll-mt-24 relative overflow-hidden rounded-[2rem] border border-slate-800/60 bg-slate-950/20 p-6 sm:p-10"
        >
          {/* local glow accents */}
          <div className="pointer-events-none absolute -inset-24 opacity-70">
            <div className="absolute left-[-10%] top-[-10%] h-[520px] w-[520px] rounded-full bg-brand-500/10 blur-3xl" />
            <div className="absolute right-[-15%] top-[5%] h-[560px] w-[560px] rounded-full bg-sky-400/10 blur-3xl" />
            <div className="absolute left-[35%] bottom-[-25%] h-[520px] w-[520px] rounded-full bg-cyan-400/10 blur-3xl" />
          </div>

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-200">
                Bonjour, je suis
              </div>
              <h1 className="mt-2 text-5xl sm:text-6xl font-extrabold tracking-tight">
                <span className="text-brand-300">Mourad Latoundji</span>
              </h1>

              {/* Mobile: photo between name and job title (layout à la Shamwwil) */}
              <div className="mt-6 flex justify-center lg:hidden">
                <div className="group relative">
                  <div className="pointer-events-none absolute -inset-3 rounded-full bg-blue-500/20 blur-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  <div className="relative rounded-full ring-1 ring-blue-400/25 transition-all duration-300 group-hover:ring-blue-400/60">
                    <img
                      src="/assets/photos/profile-without-bg.png"
                      alt="Portrait de Mourad Latoundji"
                      loading="lazy"
                      decoding="async"
                      className="h-44 w-44 sm:h-52 sm:w-52 rounded-full object-contain p-2 transition-transform duration-300 will-change-transform group-hover:scale-105 group-hover:drop-shadow-[0_0_18px_rgba(59,130,246,0.55)]"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4 text-xl sm:text-2xl font-semibold text-slate-200">{profileData.title}</div>

              {/* <p className="mt-6 max-w-2xl text-slate-300 text-base sm:text-lg leading-relaxed"> */}
                {/* TODO: Add description in JSON file*/}
                {/* Le paragraphe est désormais justifié grâce à la classe "text-justify" de Tailwind CSS */}
                <p className="mt-6 max-w-2xl text-slate-300 text-base sm:text-lg leading-relaxed text-justify">
                  {profileData.tagline}
                </p>

              {/* <div className="mt-5 text-sm text-slate-400">
                Basé en France • Ouvert aux échanges • Réponse rapide par email
              </div> */}
              <div className="mt-5 flex flex-wrap gap-2">
                {profileData.focusTags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full border border-slate-700 bg-slate-950/35 px-3 py-1 text-xs font-semibold text-slate-200"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#projects"
                  className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-brand-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                >
                  Mes projets
                </a>
                <a
                  href="#contact"
                  className="rounded-xl border border-slate-700 bg-slate-950/25 px-5 py-2.5 text-sm font-semibold text-slate-100 hover:bg-slate-950/55 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
                >
                  Contactez-moi
                </a>
              </div>
            </div>

            <div className="hidden lg:block lg:col-span-5">
              <div className="lite-card relative rounded-3xl p-5 sm:p-6">
                <div className="absolute inset-0 rounded-3xl ring-1 ring-white/10" />
                <div className="relative">
                  <div className="group overflow-hidden rounded-3xl bg-slate-950/30">
                    <img
                      src="/assets/photos/profile-without-bg.png"
                      alt="Portrait de Mourad Latoundji"
                      loading="lazy"
                      decoding="async"
                      className="h-[420px] w-full object-contain object-bottom p-6 lite-media transition-transform duration-300 will-change-transform group-hover:scale-[1.03] group-hover:drop-shadow-[0_0_22px_rgba(59,130,246,0.45)]"
                    />
                      {/* <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                        <div className="absolute inset-0 ring-1 ring-blue-400/35 group-hover:ring-blue-400/60 rounded-3xl" />
                      </div> */}
                  </div>

                  <div className="mt-5 flex flex-wrap items-center gap-2">
                    {/* TODO: Add skills */
                    /* <span className="rounded-full border border-slate-700 bg-slate-950/35 px-3 py-1 text-xs font-semibold text-slate-200">
                      C/C++
                    </span>
                    <span className="rounded-full border border-slate-700 bg-slate-950/35 px-3 py-1 text-xs font-semibold text-slate-200">
                      Python
                    </span>
                    <span className="rounded-full border border-slate-700 bg-slate-950/35 px-3 py-1 text-xs font-semibold text-slate-200">
                      Linux embarqué
                    </span>
                    <span className="rounded-full border border-slate-700 bg-slate-950/35 px-3 py-1 text-xs font-semibold text-slate-200">
                      CI/CD & tests
                    </span> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sections */}

        <section id="timeline" className="scroll-mt-24">
          <Timeline variant="lite" />
        </section>

        <section id="projects" className="scroll-mt-24">
          <ProjectsCarousel variant="lite" layout="grid" />
        </section>

        <section id="skills" className="scroll-mt-24 space-y-10">
          <Skills variant="lite" />
          {/* <Languages variant="lite" />
          <Interests variant="lite" /> */}
        </section>

        <section id="contact" className="scroll-mt-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
            <ContactForm variant="lite" />
            <div className="lite-card rounded-2xl overflow-hidden">
              <div className="p-5">
                <div className="text-xs tracking-widest uppercase text-slate-400">Réseaux & références</div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => copyToClipboard(contactData.email ?? '', 'email')}
                    className="rounded-xl border border-slate-700 bg-slate-950/30 px-4 py-3 text-sm font-semibold text-slate-100 hover:bg-slate-950/55 transition-colors flex items-center gap-2"
                  >
                    <Mail className="h-4 w-4 text-brand-300" />
                    <span className="truncate">{contactData.email ?? 'Email'}</span>
                  </button>
                  {contactData.phone && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(contactData.phone ?? '', 'phone')}
                      className="rounded-xl border border-slate-700 bg-slate-950/30 px-4 py-3 text-sm font-semibold text-slate-100 hover:bg-slate-950/55 transition-colors flex items-center gap-2"
                    >
                      <Phone className="h-4 w-4 text-brand-300" />
                      <span className="truncate">{contactData.phone}</span>
                    </button>
                  )}
                  {socials.linkedin && (
                    <a
                      href={socials.linkedin}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-xl border border-slate-700 bg-slate-950/30 px-4 py-3 text-sm font-semibold text-slate-100 hover:bg-slate-950/55 transition-colors flex items-center gap-2"
                    >
                      <Linkedin className="h-4 w-4 text-brand-300" />
                      <span>LinkedIn</span>
                      <ArrowUpRight className="ml-auto h-4 w-4 text-slate-400" />
                    </a>
                  )}
                  {socials.github && (
                    <a
                      href={socials.github}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-xl border border-slate-700 bg-slate-950/30 px-4 py-3 text-sm font-semibold text-slate-100 hover:bg-slate-950/55 transition-colors flex items-center gap-2"
                    >
                      <Github className="h-4 w-4 text-brand-300" />
                      <span>GitHub</span>
                      <ArrowUpRight className="ml-auto h-4 w-4 text-slate-400" />
                    </a>
                  )}
                </div>
{/* 
                <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-950/20 p-4">
                  <div className="text-xs tracking-widest uppercase text-slate-400">Références</div>
                  <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                    Références professionnelles disponibles sur demande. Si vous le souhaitez,
                    je peux aussi partager des recommandations via LinkedIn.
                  </p>
                </div> */}
              </div>
            </div>
          </div>
        </section>

        <div className="py-6 text-xs text-slate-400">
          <button type="button" onClick={() => scrollToSection('top')} className="font-semibold text-brand-300 hover:underline">
            Retour en haut
          </button>
        </div>
      </main>

      {/* Toast feedback (copy) */}
      <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex justify-center px-4" aria-live="polite">
        <AnimatePresence mode="wait">
          {toast && (
            <motion.div
              key={toast.id}
              initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.98 }}
              animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 10, scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 520, damping: 32 }}
              className="pointer-events-none relative overflow-hidden rounded-2xl border border-slate-700/60 bg-slate-950/55 px-4 py-2 text-sm text-slate-100 shadow-[0_16px_50px_rgba(0,0,0,0.45)] backdrop-blur"
            >
              <div className="absolute inset-0 opacity-70">
                <div className="absolute -left-10 -top-10 h-24 w-24 rounded-full bg-blue-500/20 blur-2xl" />
                <div className="absolute -right-10 -bottom-10 h-24 w-24 rounded-full bg-cyan-400/10 blur-2xl" />
              </div>
              <div className="relative flex items-center gap-2">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/25">
                  <Check className="h-4 w-4 text-emerald-300" />
                </span>
                <span className="font-semibold">{toast.message}</span>
              </div>
              {!reduceMotion && (
                <motion.div
                  aria-hidden="true"
                  initial={{ scaleX: 1 }}
                  animate={{ scaleX: 0 }}
                  transition={{ duration: 1.45, ease: 'linear' }}
                  className="relative mt-2 h-[2px] origin-left rounded-full bg-gradient-to-r from-brand-400/70 via-sky-300/50 to-transparent"
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}


